<template>
  <div id="AccountProfile" class="Main-Content">
    <ValidationObserver v-slot="{ invalid }" slim>
      <LoadingLayout v-if="loading" :page="true" />
      
        <Page
          v-if="!loading"
          :title="$t('Profile')"
          :primaryAction="{
            label: $t('Save'),
            onAction: saveProfile,
            disabled: invalid || saving || twoFactorMobileRequired
          }"
          :secondaryActions="[{
            label: $t('Notes'),
            onAction: () => eventHub.$emit('showAccountNotes', 'USER')
          }]"
        >

          <Layout v-if="theUser.accountId !== theUser.onBehalfOfAccountId">
            <LayoutSection>
              <Card>
                <CardSection :title="$t('No access')">
                  <div class="ErrorText">
                    <Icon icon="fas fas fa-exclamation-triangle fa-2x" />
                    <p>{{ $t('AccountProfile.noAccessMessage') }}</p>
                  </div>
                </CardSection>
              </Card>
            </LayoutSection>
          </Layout>

          <Layout v-if="theUser.accountId === theUser.onBehalfOfAccountId">
            <LayoutSection>
              <Card>
                <CardSection :title="$t('Contact Information')">

                  <FormLayout>
                    <validation-provider :name="$t('First Name')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('First Name')"
                        v-model="user.firstName"
                        :error="errors[0]"
                      />
                      <!-- formErrors.firstName -->
                    </validation-provider>

                    <validation-provider :name="$t('Last Name')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Last Name')"
                        v-model="user.lastName"
                        :error="errors[0]"
                      />
                      <!-- formErrors.lastName -->
                    </validation-provider>

                    <validation-provider :name="$t('Email')" immediate rules="required|email" v-slot="{ errors }">
                      <TextField
                        :label="$t('Email')"
                        v-model="user.email"
                        :error="errors[0]"
                      />
                      <!-- formErrors.email -->
                    </validation-provider>

                    <validation-provider :name="$t('Mobile Number')" immediate rules="phone" v-slot="{ errors }">
                      <TextField
                        :label="$t('Mobile Number')"
                        v-model="user.mobileNumber"
                        :error="errors[0]"
                        :help-text="$t('AccountProfile.mobileNumberHelp')"
                      >
                        <template #left>
                          <div class="mobile-code">
                            <Select
                              label=""
                              :placeholder="$t('AccountProfile.chooseCode')"
                              :options="phonePrefixValues"
                              v-model="user.mobileCountryCode"
                            />
                          </div>
                        </template>
                      </TextField>
                    </validation-provider>

                    <Select v-if="twoFactorMethods.length"
                      :label="$t('AccountProfile.preferred2FAMethod')"
                      :placeholder="$t('AccountProfile.chooseMode')"
                      :help-text="$t('AccountProfile.authCodeHelp')"
                      v-model="user.preferred2FAMode"
                      :options="twoFactorModes"
                      :error="twoFactorMobileRequired"
                    />
                  </FormLayout>
                </CardSection>
              </Card>
            </LayoutSection>
          </Layout>

      </Page>
    </ValidationObserver>

  </div>
</template>

<script>
import { extend as ValidateExtend } from 'vee-validate/dist/vee-validate.full'

export default {
  name: 'AccountProfile',
  data () {
    return {
      user: null,
      phonePrefixValues: null,
      loading: true,
      saving: false,
      twoFactorMethods: [],
      formErrors: {}
    }
  },
  mounted() {
    ValidateExtend('phone', (value) => {
      if (value && !value.match(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/)) {
        return this.$t('validation.validPhone', { field: '{_field_}' })
      }
      return true
    })
    if (this.theUser.accountId !== this.theUser.onBehalfOfAccountId) {
      this.$router.replace('/account/company/')
      return
    }

    NProgress.start()
    Promise.all([
      this.api.getCurrentUserProfile(),
      this.api.getCurrentAccountTwoFactorMethods(),
      this.api.getPhonePrefixesList()
    ])
      .then((response) => {
        let [ user, twoFactorMethods, phonePrefixValues ] = response 
        // console.log(response)
        this.user = user

        this.twoFactorMethods = twoFactorMethods || []

        this.phonePrefixValues = phonePrefixValues
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    twoFactorModes() {
      let r = []
      if (this.twoFactorMethods.indexOf('email') >= 0) {
        r.push({label: this.$t('Email'), value: 'email'})
      }
      if (this.twoFactorMethods.indexOf('mobile') >= 0) {
        r.push({label: this.$t('Mobile'), value: 'mobile'})
      }
      if (this.twoFactorMethods.indexOf('none') >= 0) {
        r.push({label: this.$t('AccountProfile.disable2FAOption'), value: 'none'})
      }
      return r
    },
    twoFactorMobileRequired() {
      if (this.user.preferred2FAMode === 'mobile' && (!this.user.mobileCountryCode || !this.user.mobileNumber)) {
        return 'You must have a phone number to use mobile 2FA.'
      }
      return null
    }
  },
  methods: {
    saveProfile() {
      NProgress.start()
      this.saving = true
      this.api.updateCurrentUserProfile(this.user)
        .then(() => {
          this.$ui.toast({ title: this.$t('AccountProfile.profileSaved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    }
  }
}

</script>

<style lang="scss">
#AccountProfile {
  .mobile-code {
    width: 150px;
  }
}
</style>